import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import NestableClickable from 'components/Common/NestableClickable'
import { ImageParams } from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useContext, useRef } from 'react'
import styled from 'styled-components'
import TextButton from '../Button/TextButton'
import LineImagesIcon from '../Icons/line/LineImagesIcon'
import SolidPlayIcon from '../Icons/solid/SolidPlayIcon'
import ImageCarousel from './ImageCarousel'
import ImageGalleryLightbox from './ImageGalleryLightbox'
import VideoLightbox from './VideoLightbox'
import { ImageCarouselRefInterface } from './BaseImageCarousel'
import ModalContext from 'contexts/ModalContext'

const Container = styled(NestableClickable)`
  position: relative;

  aspect-ratio: var(--gallery-xs-aspect);

  ${mediaQueryUp.tablet} {
    aspect-ratio: var(--gallery-sm-aspect, var(--gallery-xs-aspect));
  }

  ${mediaQueryUp.desktop} {
    aspect-ratio: var(--gallery-md-aspect, var(--gallery-sm-aspect, var(--gallery-xs-aspect)));
  }

  ${mediaQueryUp.largeDesktop} {
    aspect-ratio: var(--gallery-lg-aspect, var(--gallery-md-aspect, var(--gallery-sm-aspect, var(--gallery-xs-aspect))));
  }
`

const GalleryButtons = styled(CSSBreakpoint)`
  display: flex;
  pointer-events: auto;
  gap: ${rem(8)};
`

interface Props extends React.ComponentProps<typeof ImageCarousel> {
  videoId?: string
  size?: 'small' | 'large'
}

const defaultImageProps: ImageParams = { mobileAspectRatio: '3:2', tabletAspectRatio: '16:9', quality: 'good', fit: 'none' }

function ImageGallery(props: Props) {
  const {
    images,
    className,
    videoId,
    size = 'large',
    imageParams = defaultImageProps,
    defaultIndex = 0,
    showImageTitles = true,
    ...carouselProps
  } = props

  const carouselRef = useRef<ImageCarouselRefInterface>(null)
  const showLightbox = useContext(ModalContext)

  const showImageLightbox = async() => {
    const defaultIndex = carouselRef.current ? carouselRef.current?.getCurrentImageIndex() - (videoId ? 1 : 0) : 0
    await showLightbox(<ImageGalleryLightbox
      images={images}
      defaultIndex={defaultIndex}
      onImageChange={(index, image) => {
        if (image) {
          carouselRef.current?.goToImage(image)
        }
      }}
    />)
  }
  const showVideoLightbox = () => {
    if (videoId) {
      showLightbox(<VideoLightbox videoId={videoId} playOnMount />)
    }
  }

  return (<Container
    onClick={() => showImageLightbox()}
    className={className}
    style={{
      '--gallery-xs-aspect': imageParams.mobileAspectRatio?.replace(':', ' / '),
      '--gallery-sm-aspect': imageParams.tabletAspectRatio?.replace(':', ' / '),
      '--gallery-md-aspect': imageParams.desktopAspectRatio?.replace(':', ' / '),
      '--gallery-xl-aspect': imageParams.largeDesktopAspectRatio?.replace(':', ' / '),
    }}
  >
    <ImageCarousel
      {...carouselProps}
      defaultIndex={defaultIndex}
      showImageTitles={showImageTitles}
      carouselRef={carouselRef}
      images={images}
      imageParams={imageParams}
      bottomRight={<>
        {size === 'large' && <GalleryButtons min="tablet">
          {videoId && <TextButton
            kind="secondary"
            variant="dark"
            startIcon={<SolidPlayIcon />}
            onClick={showVideoLightbox}
          >
            Play video
          </TextButton>}
          {images.length > 1 && <TextButton
            kind="secondary"
            variant="dark"
            startIcon={<LineImagesIcon />}
            nonInteractable
          >
            View gallery
          </TextButton>}
        </GalleryButtons>}
        <GalleryButtons max={size === 'large' ? 'mobile' : undefined}>
          {videoId && <TextButton
            kind="secondary"
            variant="dark"
            startIcon={<SolidPlayIcon />}
            onClick={showVideoLightbox}
          >
            Play video
          </TextButton>}
        </GalleryButtons>
      </>}
    />
  </Container>)
}

export default ImageGallery
